import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Check, ArrowLeft, ArrowRight, Sparkles } from "lucide-react";
import Wordmark from "@/components/Wordmark";

const TIERS = [
  {
    id: "free",
    name: "Free",
    tag: "for curious learners",
    price: "£0",
    period: "forever",
    features: [
      "5 recordings / month",
      "3 generations / recording",
      "Public study groups",
      "Basic notes & flashcards",
    ],
    cta: "Start free",
  },
  {
    id: "starter",
    name: "Starter",
    tag: "for serious students",
    price: "£5",
    period: "/ month",
    features: [
      "50 recordings / month",
      "10 generations / recording",
      "Private flashcards",
      "1:1 AI Study Coach",
      "10 study groups",
    ],
    cta: "Upgrade to Starter",
    popular: true,
  },
  {
    id: "pro",
    name: "Pro",
    tag: "for relentless learners",
    price: "£12",
    period: "/ month",
    features: [
      "Unlimited recordings",
      "Worksheets generator",
      "Advanced analytics",
      "Customized study plans",
      "Priority generation",
      "Everything in Starter",
    ],
    cta: "Go Pro",
  },
];

const Pricing = () => {
  const { user } = useAuth();
  const nav = useNavigate();
  const [busy, setBusy] = useState(null);

  const subscribe = async (pkg) => {
    if (!user) { nav("/signup"); return; }
    setBusy(pkg);
    try {
      const { data } = await api.post("/billing/checkout", {
        package_id: pkg,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
      setBusy(null);
    }
  };

  return (
    <div className="relative min-h-screen bg-black text-white overflow-x-hidden">
      <div className="holo-mesh opacity-70" aria-hidden />

      {/* sticky nav */}
      <nav className="sticky top-0 z-40 backdrop-blur-2xl bg-black/60 border-b border-white/[0.06]">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-6 md:px-10 py-4">
          <Link to="/" data-testid="brand-link"><Wordmark /></Link>
          <Link to="/" className="inline-flex items-center gap-2 text-neutral-500 hover:text-white transition-colors text-[0.82rem]">
            <ArrowLeft size={13} /> back
          </Link>
        </div>
      </nav>

      <section className="relative z-10 max-w-6xl mx-auto px-6 md:px-10 py-20">
        <div className="max-w-3xl mb-16">
          <div className="label-mono mb-4">/ pricing</div>
          <h1 className="font-display text-4xl md:text-6xl tightest font-semibold leading-[1.02] mb-6">
            Pay for the <span className="chrome-text">study system</span> —<br />
            not another subscription.
          </h1>
          <p className="text-neutral-400 text-base max-w-xl">
            Simple monthly plans. Cancel any time, from your account. No annual lock-in, no seat minimums, no hidden add-ons.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          {TIERS.map((t) => {
            const isCurrent = user?.plan === t.id;
            return (
              <div
                key={t.id}
                className={`relative rounded-[4px] p-8 flex flex-col ${
                  t.popular
                    ? "bg-gradient-to-b from-white to-neutral-200 text-black"
                    : "glass"
                }`}
                data-testid={`tier-${t.id}`}
              >
                {t.popular && (
                  <div className="absolute -top-2.5 left-8 px-2 py-0.5 bg-black text-white text-[9px] font-mono tracking-[0.2em] rounded-[2px] flex items-center gap-1">
                    <Sparkles size={10} className="text-amber-400" /> MOST POPULAR
                  </div>
                )}

                <div className={`text-[0.68rem] font-mono tracking-[0.2em] uppercase mb-2 ${t.popular ? "text-neutral-500" : "text-neutral-500"}`}>
                  {t.name}
                </div>
                <div className={`text-[0.85rem] mb-6 ${t.popular ? "text-neutral-600" : "text-neutral-500"}`}>
                  {t.tag}
                </div>

                <div className="flex items-baseline gap-2 mb-8">
                  <span className="font-display text-6xl tightest font-semibold tabular leading-none">{t.price}</span>
                  <span className={`text-[0.85rem] ${t.popular ? "text-neutral-600" : "text-neutral-500"}`}>{t.period}</span>
                </div>

                <ul className="space-y-3 mb-8 flex-1">
                  {t.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-[0.86rem]">
                      <Check size={14} className={`mt-0.5 shrink-0 ${t.popular ? "text-black" : "text-white"}`} />
                      <span className={t.popular ? "text-neutral-800" : "text-neutral-200"}>{f}</span>
                    </li>
                  ))}
                </ul>

                {isCurrent ? (
                  <button
                    className={`w-full py-2.5 px-4 rounded-[2px] text-[0.85rem] border ${t.popular ? "bg-black text-white border-black" : "border-white/15 text-neutral-400"}`}
                    disabled
                  >
                    Current plan
                  </button>
                ) : t.id === "free" ? (
                  <Link to={user ? "/dashboard" : "/signup"} data-testid={`cta-${t.id}`}>
                    <button className={`w-full py-2.5 px-4 rounded-[2px] text-[0.85rem] inline-flex items-center justify-center gap-2 transition-colors ${t.popular ? "bg-black text-white hover:bg-neutral-800" : "border border-white/20 hover:bg-white/[0.04]"}`}>
                      {t.cta} <ArrowRight size={13} />
                    </button>
                  </Link>
                ) : (
                  <button
                    onClick={() => subscribe(t.id)}
                    disabled={busy === t.id}
                    data-testid={`cta-${t.id}`}
                    className={`w-full py-2.5 px-4 rounded-[2px] text-[0.85rem] font-medium inline-flex items-center justify-center gap-2 transition-colors disabled:opacity-50 ${
                      t.popular
                        ? "bg-black text-white hover:bg-neutral-800"
                        : "bg-white text-black hover:bg-neutral-100"
                    }`}
                  >
                    {busy === t.id ? "Redirecting…" : <>{t.cta} <ArrowRight size={13} /></>}
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {/* fine print row */}
        <div className="mt-16 grid md:grid-cols-3 gap-6 text-[0.82rem] text-neutral-500">
          <FinePrint title="Secure checkout" desc="Stripe hosted checkout. Your card never touches our servers." />
          <FinePrint title="Cancel any time" desc="Self-service billing portal, right from your account." />
          <FinePrint title="Fair usage" desc="Recording caps reset monthly. No overage charges." />
        </div>
      </section>
    </div>
  );
};

const FinePrint = ({ title, desc }) => (
  <div className="border-t border-white/[0.06] pt-4">
    <div className="text-white text-[0.86rem] font-medium mb-1">{title}</div>
    <div>{desc}</div>
  </div>
);

export default Pricing;
